<template>
  <va-sidebar
    v-model="writableVisible"
    :width="sidebarWidth"
    :color="color"
    minimized-width="0"
    class="sidebar-container"
  >
    <!-- <div class="sidebar-notification-badge" style="text-align:center; margin: 10px 0;">
      <NotificationBadge :count="unreadCount" />
    </div> -->
    <div class="sidebar-content">
      <va-accordion v-model="value" multiple>
        <va-collapse v-for="(route, index) in filteredRoutes" :key="index">
          <!-- Rotas filtradas -->
          <template #header="{ value: isCollapsed }">
            <va-sidebar-item
              :to="route.children ? undefined : { name: route.name }"
              :active="routeHasActiveChild(route)"
              :active-color="activeColor"
              :text-color="textColor(route)"
              :aria-label="`${route.children ? 'Open category ' : 'Visit'} ${t(route.displayName)}`"
              role="button"
              hover-opacity="0.10"
            >
              <va-sidebar-item-content class="py-3 pr-2 pl-4">
                <va-icon
                  v-if="route.meta.icon"
                  aria-hidden="true"
                  :name="route.meta.icon"
                  size="20px"
                  :color="iconColor(route)"
                />
                <va-sidebar-item-title class="flex justify-between items-center leading-5 font-semibold">
                  {{ t(route.displayName) }}
                  <va-icon
                    v-if="route.displayName == 'menu.crm' && received"
                    name="fiber_manual_record"
                    :color="iconColor(route)"
                  />
                  <va-icon v-if="route.children" :name="arrowDirection(isCollapsed)" size="20px" />
                </va-sidebar-item-title>
              </va-sidebar-item-content>
            </va-sidebar-item>
          </template>
          <template #body>
            <div v-for="(childRoute, index2) in route.children" :key="index2">
              <va-sidebar-item
                :to="{ name: childRoute.name }"
                :active="isActiveChildRoute(childRoute)"
                :active-color="activeColor"
                :text-color="textColor(childRoute)"
                :aria-label="`Visit ${t(route.displayName)}`"
                hover-opacity="0.10"
              >
                <va-sidebar-item-content class="py-3 pr-2 pl-11">
                  <va-sidebar-item-title class="leading-5 font-semibold">
                    {{ t(childRoute.displayName) }}
                  </va-sidebar-item-title>
                </va-sidebar-item-content>
              </va-sidebar-item>
            </div>
          </template>
        </va-collapse>
      </va-accordion>
    </div>

    <div class="sidebar-footer">
      <span class="va-title sidebar-version"> {{ appVersion }} </span>
    </div>
  </va-sidebar>
</template>

<script lang="ts">
import { defineComponent, watch, ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'
import { useChatStore } from '../../stores/chatStore'

import { permissionStore } from '../../stores/permissions'

import navigationRoutes, { type INavigationRoute } from './NavigationRoutes'
import { EventBus } from '../../services/eventBus'

export default defineComponent({
  name: 'Sidebar',
  props: {
    visible: { type: Boolean, default: true },
    mobile: { type: Boolean, default: false },
  },
  emits: ['update:visible'],

  setup: (props, { emit }) => {
    const received = ref(false)

    EventBus.on('new-message', (data: any) => {
      received.value = true
    })

    EventBus.on('notification', (data: any) => {
      received.value = true
    })

    EventBus.on('iSawTheNotification', () => {
      received.value = false
    })

    const chatStore = useChatStore()
    const unreadCount = computed(() => chatStore.unreadCount)

    const filteredRoutes = computed(() => filterRoutes(navigationRoutes.routes))

    const { getColor, colorToRgba } = useColors()
    const route = useRoute()
    const { t } = useI18n()

    const value = ref<boolean[]>([])

    const writableVisible = computed({
      get: () => props.visible,
      set: (v: boolean) => emit('update:visible', v),
    })

    const isActiveChildRoute = (child: INavigationRoute) => route.name === child.name

    const routeHasActiveChild = (section: INavigationRoute) => {
      if (!section.children) {
        return route.path.endsWith(`${section.name}`)
      }
      return section.children.some(({ name }) => route.path.endsWith(`${name}`))
    }

    const setActiveExpand = () =>
      (value.value = filteredRoutes.value.map((route: INavigationRoute) => routeHasActiveChild(route)))

    const sidebarWidth = computed(() => (props.mobile ? '100vw' : '230px'))
    const color = computed(() => getColor('background-secondary'))
    const activeColor = computed(() => colorToRgba(getColor('focus'), 0.1))

    const iconColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : 'secondary')
    const textColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : 'textPrimary')
    const arrowDirection = (state: boolean) => (state ? 'va-arrow-up' : 'va-arrow-down')

    watch(() => route.fullPath, setActiveExpand, { immediate: true })

    // Versão automaticamente injetada pelo vite.config.ts
    const appVersion = computed(() => {
      return import.meta.env.VITE_VERSION || 'v1.0.0'
    })

    return {
      writableVisible,
      sidebarWidth,
      value,
      color,
      activeColor,
      filteredRoutes,
      routeHasActiveChild,
      isActiveChildRoute,
      t,
      iconColor,
      textColor,
      arrowDirection,
      received,
      unreadCount,
      appVersion,
    }
  },
})

const filterRoutes = (routes: INavigationRoute[]) => {
  const perm = permissionStore()

  return routes.filter((route) => {
    if (!route.permission) {
      return true
    }

    const hasPermission = perm.hasPermission(route.permission)

    return hasPermission
  })
}
</script>

<style scoped>
.sidebar-container :deep(.va-sidebar__content) {
  display: flex;
  flex-direction: column;
  height: 100vh;
}

.sidebar-content {
  flex: 1;
  overflow-y: auto;
}

.sidebar-footer {
  padding: 1rem;
  border-top: 1px solid rgba(var(--va-primary-rgb), 0.1);
  background-color: var(--va-background-secondary);
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: auto;
}

.sidebar-version {
  color: var(--va-primary) !important;
  font-size: 0.875rem;
  font-weight: 500;
  text-align: center;
  transition: opacity 0.2s ease;
}

.sidebar-version:hover {
  opacity: 1;
}
</style>
